import { atom, selector } from 'recoil';
import { ORDER_ASCENDING, ORDER_DESCENDING } from '../constants/app';
import {
  CURRENT_STATE_SORT_ADDRESS,
  CURRENT_STATE_SORT_BACKUP_BATTERY,
  CURRENT_STATE_SORT_DATE,
  CURRENT_STATE_SORT_HOUR_METER,
  CURRENT_STATE_SORT_IDENTIFIER,
  CURRENT_STATE_SORT_LABEL,
  CURRENT_STATE_SORT_LICENSE_PLATE,
  CURRENT_STATE_SORT_ODOMETER,
  CURRENT_STATE_SORT_POWERSOURCE,
  CURRENT_STATE_SORT_SATELLITES,
  CURRENT_STATE_SORT_SIGNAL,
  CURRENT_STATE_SORT_SPEED,
  CURRENT_STATE_SORT_STATUS,
  CURRENT_STATE_SORT_TYPE,
} from '../constants/currentStates';
import { OrderDirection } from '../types/app';
import { CurrentState, CurrentStateSortOption } from '../types/currentState';
import { isDevice } from '../utils/currentStates';
import { currentStatesState } from './client';
import { groupState } from './groups';

export const loadingCurrentStatesState = atom({
  key: 'app.currentStates.loading',
  default: false,
});

export const searchCurrentStateState = atom({
  key: 'app.currentStates.search',
  default: '',
});

export const orderDirectionState = atom<OrderDirection>({
  key: 'app.currentStates.orderDirection',
  default: ORDER_DESCENDING,
});

export const sortCurrentStateState = atom<CurrentStateSortOption>({
  key: 'app.currentStates.sort',
  default: CURRENT_STATE_SORT_LABEL,
});

export const filterGroupState = atom<string>({
  key: 'app.currentStates.filter.group',
  default: '',
});

export const filteredCurrentStateIdsState = selector<string[] | undefined>({
  key: 'app.currentStateIds.filtered',
  get: ({ get }) => {
    const currentStates = get(filteredCurrentStatesState);
    if (!currentStates) return;

    return currentStates.map((currentState) => currentState.ident);
  },
});

export const updatingCurrentStatesState = selector<boolean>({
  key: 'app.currentStates.updating',
  get: ({ get }) => {
    const currentStates = get(filteredCurrentStatesState);
    if (!currentStates) return false;
    return get(loadingCurrentStatesState);
  },
});

const filteredCurrentStatesState = selector<CurrentState[] | undefined>({
  key: 'app.currentStates.filtered',
  get: ({ get }) => {
    const currentStates = get(currentStatesState);
    if (!currentStates) return;
    const filterGroup = get(filterGroupState);
    let filteredCurrentStates = [...currentStates];
    if (filterGroup) {
      const group = get(groupState(filterGroup));
      if (group) {
        filteredCurrentStates = filteredCurrentStates.filter((currentState) =>
          group.assignedAssets.includes(currentState.asset._id),
        );
      }
    }
    const sort = get(sortCurrentStateState);
    const sortCurrentStates = (a: CurrentState, b: CurrentState) => {
      if (sort === CURRENT_STATE_SORT_LABEL) {
        if (a.asset.label) {
          return a.asset.label?.localeCompare(b.asset.label || '');
        }
        return -1;
      }
      if (sort === CURRENT_STATE_SORT_DATE) {
        return +a.timestamp - +b.timestamp;
      }
      if (sort === CURRENT_STATE_SORT_ODOMETER) {
        return a.gnss_vehicle_mileage - b.gnss_vehicle_mileage;
      }
      if (sort === CURRENT_STATE_SORT_HOUR_METER) {
        return -1;
      }
      if (sort === CURRENT_STATE_SORT_STATUS) {
        return +a.engine_ignition_status - +b.engine_ignition_status;
      }
      if (sort === CURRENT_STATE_SORT_SPEED) {
        return a.position_speed - b.position_speed;
      }
      if (sort === CURRENT_STATE_SORT_BACKUP_BATTERY) {
        return +a.battery_percentage - +b.battery_percentage;
      }
      if (sort === CURRENT_STATE_SORT_POWERSOURCE) {
        return a.external_powersource_voltage - b.external_powersource_voltage;
      }
      if (sort === CURRENT_STATE_SORT_SATELLITES) {
        return a.position_satellites - b.position_satellites;
      }
      if (sort === CURRENT_STATE_SORT_SIGNAL) {
        return a.gsm_signal_dbm - b.gsm_signal_dbm;
      }
      if (sort === CURRENT_STATE_SORT_TYPE) {
        return a.positionType.localeCompare(b.positionType);
      }
      if (sort === CURRENT_STATE_SORT_ADDRESS) {
        return a.geoReference.localeCompare(b.geoReference);
      }
      if (sort === CURRENT_STATE_SORT_LICENSE_PLATE && a.asset.vehicle?.licensePlate) {
        return a.asset.vehicle?.licensePlate.localeCompare(b.asset.vehicle?.licensePlate || '');
      }
      if (sort === CURRENT_STATE_SORT_IDENTIFIER && a.asset.container?.identifier) {
        return a.asset.container.identifier.localeCompare(b.asset.container?.identifier || '');
      }
      return -1;
    };

    const search = get(searchCurrentStateState);

    if (search) {
      filteredCurrentStates = filteredCurrentStates.filter((currentState) => {
        const lowerCasedSearch = search.toLowerCase();
        if (
          (isDevice(currentState.asset.device) &&
            currentState.asset.device.ident.toLowerCase().includes(lowerCasedSearch)) ||
          currentState.asset.label?.toLowerCase().includes(lowerCasedSearch) ||
          currentState.asset.vehicle?.licensePlate?.toLowerCase().includes(lowerCasedSearch) ||
          currentState.asset.container?.identifier?.toLowerCase().includes(lowerCasedSearch)
        )
          return true;
        return false;
      });
    }

    const sortedCurentStates = filteredCurrentStates.sort(sortCurrentStates);
    const orderDirection = get(orderDirectionState);

    return orderDirection === ORDER_ASCENDING ? sortedCurentStates : sortedCurentStates.reverse();
  },
});

export const currentStatesPoweredOnState = selector<number>({
  key: 'app.currentStates.poweredOn',
  get: ({ get }) => {
    const currentStates = get(filteredCurrentStatesState);
    if (!currentStates) return 0;
    return currentStates.filter((currentState) => currentState.engine_ignition_status).length;
  },
});

export const currentStatesPoweredOffState = selector<number>({
  key: 'app.currentStates.poweredOff',
  get: ({ get }) => {
    const currentStates = get(filteredCurrentStatesState);
    if (!currentStates) return 0;
    return currentStates.filter((currentState) => !currentState.engine_ignition_status).length;
  },
});
